<!-- eslint-disable vue/html-self-closing -->
<template>
  <div
    :class="['riseup-header', {
      'green-background': isGreen && !forceBackgroundColor,
      'etoro': isEtoroVariant,
      'etoro-green': isEtoroVariant && isGreen,
      'prograd': isProgradVariant,
      'prograd-background': isProgradVariant && !forceBackgroundColor,
      'salary-finance': isSalaryFinanceVariant,
      'syndesi': isSyndesiVariant,
      'riseup-wl': isRiseupWLVariant,
    }]"
    :style="{ backgroundColor: forceBackgroundColor }"
  >
    <div
      v-if="isEtoroVariant"
      class="logo-container"
    >
      <img
        v-if="isEtoroVariant && !isGreen"
        :src="eToroLogo"
        class="etoro"
      />
      <img
        v-if="isEtoroVariant && isGreen"
        :src="eToroLogoWhite"
        class="etoro"
      />
      <img
        v-if="isEtoroVariant && !isGreen"
        src="../../assets/powered-by.svg"
        class="powered-logo"
        alt="powered by"
      />
      <img
        v-if="isEtoroVariant && isGreen"
        src="../../assets/powered-by-white.svg"
        class="powered-logo"
        alt="powered by"
      />
    </div>
    <div
      v-else-if="isProgradVariant"
      class="logo-container"
    >
      <img
        src="../../assets/prograd/prograd-logo.png"
        class="logo"
      />
      <img
        src="../../assets/powered-by.svg"
        class="powered-logo"
        alt="powered by"
      />
    </div>
    <div
      v-else-if="isSalaryFinanceVariant"
      class="logo-container"
    >
      <img
        src="../../assets/salary-finance/salary-finance-logo.svg"
        class="logo"
      />
      <img
        v-if="!isGreen"
        src="../../assets/powered-by.svg"
        class="powered-logo"
        alt="powered by"
      />
      <img
        v-if="isGreen"
        src="../../assets/powered-by-white.svg"
        class="powered-logo"
        alt="powered by"
      />
    </div>
    <div
      v-else-if="isSyndesiVariant"
      class="logo-container"
    >
      <img
        v-if="!isGreen"
        :src="syndesiDarkLogoSrc"
        style="width: 190px; margin-top: 8px; height: 27px;"
        class="logo"
      />
      <img
        v-if="!isGreen"
        src="../../assets/powered-by.svg"
        class="powered-logo"
        alt="powered by"
      />
      <img
        v-if="isGreen"
        :src="syndesiLightLogoSrc"
        class="logo"
        style="width: 190px; margin-top: 8px; height: 27px;"
      />
      <img
        v-if="isGreen"
        src="../../assets/powered-by-white.svg"
        class="powered-logo"
        alt="powered by"
      />
    </div>
    <div
      v-else-if="isRiseupWLVariant"
      class="logo-container"
    >
      <img
        v-if="!isGreen"
        :src="riseupWLBlueLogoIcon"
        class="logo"
      />
      <img
        v-if="isGreen"
        :src="riseupWLWhiteLogoIcon"
        class="logo"
      />
    </div>
    <div v-else>
      <img
        v-if="!isEtoroVariant"
        class="logo"
        :src="logoSrc"
        @click="handleLogoClick"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue';
import eToroLogoWhite from '../../assets/etoro/etoro-logo-white.svg';
import eToroLogo from '../../assets/etoro/etoro-logo.svg';
import riseupLogoIcon from '../../assets/riseup-logo.svg';
import riseupWhiteLogoIcon from '../../assets/riseup-white-logo.svg';
import riseupWLBlueLogoIcon from '../../assets/riseup-wl-blue-logo.svg';
import riseupWLWhiteLogoIcon from '../../assets/riseup-wl-white-logo.svg';
import syndesiAbcDarkLogo from '../../assets/syndesi/abc-syndesi-dark-logo.svg';
import syndesiAbcLightLogo from '../../assets/syndesi/abc-syndesi-light-logo.svg';
import syndesiDnsDarkLogo from '../../assets/syndesi/dns-syndesi-dark-logo.svg';
import syndesiDnsLightLogo from '../../assets/syndesi/dns-syndesi-light-logo.svg';
import { FlowConfigIdType, WhiteLabelFirmIdType } from '../../types/otsConfig';

const props = defineProps<{
  isGreen?: boolean;
  flowConfigId?: FlowConfigIdType;
  forceBackgroundColor?: string;
  whiteLabelFirmId?: WhiteLabelFirmIdType;
}>();

const isGreen = computed(() => props.isGreen);

const forceBackgroundColor = computed(() => props.forceBackgroundColor);

const logoSrc = computed(() => (isGreen.value ? riseupWhiteLogoIcon : riseupLogoIcon));

const isEtoroVariant = computed(() => props.flowConfigId === FlowConfigIdType.Etoro);
const isProgradVariant = computed(() => props.flowConfigId === FlowConfigIdType.Prograd);
const isSalaryFinanceVariant = computed(() => props.flowConfigId === FlowConfigIdType.SalaryFinance);
const isSyndesiVariant = computed(() => props.flowConfigId === FlowConfigIdType.Syndesi);
const isRiseupWLVariant = computed(() => props.flowConfigId === FlowConfigIdType.RiseupWL);

const syndesiDarkLogoSrc = computed(() => {
  if (props.whiteLabelFirmId && props.whiteLabelFirmId === WhiteLabelFirmIdType.DefaultFirm) {
    return syndesiAbcDarkLogo;
  }
  return syndesiDnsDarkLogo;
});

const syndesiLightLogoSrc = computed(() => {
  if (props.whiteLabelFirmId && props.whiteLabelFirmId === WhiteLabelFirmIdType.DefaultFirm) {
    return syndesiAbcLightLogo;
  }
  return syndesiDnsLightLogo;
});
const handleLogoClick = () => {
  window.location.href = 'https://start.letsriseup.com/?utm_source=ots';
};
</script>

<style lang="scss" scoped>
@import '../../scss/riseup-vars.scss';

.riseup-header {
  position: sticky;
  top: 0;
  width: 100%;
  height: $riseup_header_height; // Ensure this value is set
  border-bottom: 1px solid $riseup_gray_0;
  background-color: $main_background_color;
  display: flex;
  align-items: center;
  z-index: 1000;
  padding: 25px;

  &.green-background {
    background-color: $green_background_color;
    border-bottom: 1px solid $riseup_gray_1;
  }

  &.etoro {
    background-color: #f6f8ed;
    border-bottom: 0;
    flex-direction: row;
  }

  &.etoro-green {
    background-color: #14c536;
    border-bottom: 0;
    flex-direction: row;
  }

  &.prograd {
    background-color: #faecea;
    border-bottom: 0;
    flex-direction: row;
  }

  &.salary-finance {
    background-color: #eef2f8;
    border-bottom: 0;
    flex-direction: row;
  }

  &.syndesi {
    background-color: #FCF7ED;
    border-bottom: 0;
    flex-direction: row;
  }

  &.riseup-wl {
    background-color: #F8F9FA;
    border-bottom: 0;
    flex-direction: row;
  }

  .logo {
    width: 95px;
  }

  .prograd {
    width: 100px;
  }

  .logo-container {
    display: flex;
    align-items: center;
    width: 100%;
  }

  .etoro {
    width: 83px;
  }

  .powered-logo {
    width: 53px;
    margin-left: auto;
  }
}
</style>
